import React, { useEffect, useState } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { StationDropdown } from "./StationDropdown";
import { ActionDropdown } from "./ActionDropdown";
import { TriggerTypeDropdown } from "./TriggerTypeDropdown";
import { ActionItem } from "./ActionItem";
import { MissionStopDetailsProps, TriggerType } from "../types";
import {
  ActionControlsContainer,
  DragHandle,
  ExampleMissionStepRow,
  MissionStepControls,
  MissionStepRowContent,
  RemoveActionIcon,
  RemoveStepIcon,
  UnhitchCheckbox,
  UnhitchCheckboxContainer,
  UnhitchCheckboxLabel,
  UnhitchCheckboxRing,
  UnhitchCheckboxText,
  UnhitchCheckmarkRing,
} from "../styles";
import { ReactComponent as CloseIconSvg } from "../assets/close.svg";
import { ReactComponent as CheckboxIconSvg } from "../assets/Checkbox.svg";
import { ReactComponent as CheckboxFilledIconSvg } from "../assets/checkbox-filled.svg";
import { ReactComponent as DragIconSvg } from "../assets/drag.svg";
import { CONFIRMATION_TYPES, TRIGGER_TYPES } from "Components/common/constants";

const EMPTY_DROP_ZONE_HEIGHT = 8; // minimal height so empty list is still droppable

export const MissionStopDetails: React.FC<MissionStopDetailsProps> = ({
  stepIndex,
  missionStop,
  stops,
  availableActions,
  availableTriggerTypes,
  onStationSelect,
  onActionSelect,
  onTriggerTypeSelect,
  onDurationChange,
  onRemoveAction,
  onRemoveStep,
  onUnhitchChange,
  isStepEnabled,

  isDragDisabled = false,
}) => {
  const [selectedStation, setSelectedStation] = useState<Stop | null>(null);
  const [isUnhitch, setIsUnhitch] = useState(false);

  useEffect(() => {
    if (missionStop) {
      const foundStation = stops.filter(
        (stop) => stop.id === missionStop.stopPk
      )[0];

      setSelectedStation(foundStation);
      setIsUnhitch(missionStop.hitchType === 2);

      // If we have a stopPk but can't find the station, it might be invalid
      if (missionStop.stopPk && !foundStation) {
        console.warn("Invalid station found:", missionStop.stopPk);
      }
    }
  }, [missionStop, stops]);

  const handleUnhitchChange = () => {
    if (isStepEnabled) {
      const newUnhitchState = !isUnhitch;
      setIsUnhitch(newUnhitchState);
      onUnhitchChange(stepIndex, newUnhitchState);
    }
  };

  // Check if this stop has double confirmation
  const hasDoubleConfirmation =
    missionStop?.confirmationType ===
    CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION;

  return (
    <Draggable
      draggableId={`step-${stepIndex}`}
      index={stepIndex}
      isDragDisabled={isDragDisabled}
    >
      {(provided, snapshot) => (
        <ExampleMissionStepRow
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          <MissionStepRowContent>
            <DragHandle {...provided.dragHandleProps} disabled={isDragDisabled}>
              <DragIconSvg />
            </DragHandle>
            <MissionStepControls>
              <StationDropdown
                stations={stops}
                selectedStation={selectedStation}
                onStationSelect={(station) =>
                  onStationSelect(station, stepIndex)
                }
                disabled={!isStepEnabled}
              />
              <UnhitchCheckboxContainer>
                <UnhitchCheckbox
                  onClick={handleUnhitchChange}
                  style={
                    !isStepEnabled
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : {}
                  }
                >
                  <UnhitchCheckboxRing>
                    <UnhitchCheckmarkRing>
                      {isUnhitch ? (
                        <CheckboxFilledIconSvg />
                      ) : (
                        <CheckboxIconSvg />
                      )}
                    </UnhitchCheckmarkRing>
                  </UnhitchCheckboxRing>
                  <UnhitchCheckboxText>
                    <UnhitchCheckboxLabel>Unhitch</UnhitchCheckboxLabel>
                  </UnhitchCheckboxText>
                </UnhitchCheckbox>
              </UnhitchCheckboxContainer>
            </MissionStepControls>

            <RemoveStepIcon onClick={() => onRemoveStep(stepIndex)}>
              <CloseIconSvg />
            </RemoveStepIcon>
          </MissionStepRowContent>

          {/* Show dismissible warning message when double confirmation is just selected */}
          {/* Removed as per edit hint */}

          {/* Show double confirmation action when selected */}
          {hasDoubleConfirmation && (
            <ActionControlsContainer>
              <ActionDropdown
                actions={[
                  {
                    actionType: "WAIT_FOR_TRIGGER",
                    description: "Wait for trigger",
                  },
                ]}
                selectedAction={{
                  actionType: "WAIT_FOR_TRIGGER",
                  description: "Wait for trigger",
                }}
                onActionSelect={() => {
                  // No-op since it's locked - do nothing
                }}
                disabled={true}
              />
              <TriggerTypeDropdown
                triggerTypes={availableTriggerTypes}
                selectedTriggerType={{
                  key: TRIGGER_TYPES.DOUBLE_CONFIRMATION,
                  value: "Double confirmation",
                }}
                onTriggerTypeSelect={(selectedTriggerType: TriggerType) => {
                  // Allow changing from double confirmation to other trigger types
                  if (
                    selectedTriggerType.key !==
                    TRIGGER_TYPES.DOUBLE_CONFIRMATION
                  ) {
                    // This will trigger the logic to clear double confirmation and add a new VDA action
                    onTriggerTypeSelect(selectedTriggerType, stepIndex, 0);
                  }
                }}
                disabled={!isStepEnabled}
              />
              <RemoveActionIcon
                onClick={() => {
                  if (isStepEnabled) {
                    // Remove double confirmation by clearing the confirmation type
                    onRemoveAction(stepIndex, -1); // Use -1 to indicate double confirmation removal
                  }
                }}
                style={
                  !isStepEnabled ? { opacity: 0.5, cursor: "not-allowed" } : {}
                }
              >
                <CloseIconSvg />
              </RemoveActionIcon>
            </ActionControlsContainer>
          )}

          {/* Actions (drag & drop enabled even when empty to allow cross-stop drops) */}
          {missionStop &&
            missionStop.confirmationType !==
              CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION && (
              <Droppable droppableId={`actions-${stepIndex}`} type="ACTION">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      width: "100%",
                      minHeight: EMPTY_DROP_ZONE_HEIGHT,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      opacity: isStepEnabled ? 1 : 0.5,
                    }}
                  >
                    {missionStop.missionStopVdaActions.map(
                      (actionWithTrigger, actionIndex) => {
                        return (
                          <ActionItem
                            key={actionIndex}
                            actionIndex={actionIndex}
                            action={actionWithTrigger}
                            availableActions={availableActions}
                            availableTriggerTypes={availableTriggerTypes}
                            onActionSelect={onActionSelect}
                            onTriggerTypeSelect={onTriggerTypeSelect}
                            onDurationChange={onDurationChange}
                            onRemoveAction={onRemoveAction}
                            stepIndex={stepIndex}
                            isStepEnabled={isStepEnabled}
                          />
                        );
                      }
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
        </ExampleMissionStepRow>
      )}
    </Draggable>
  );
};
